// Facebook Pixel utility for tracking events
declare global {
  interface Window {
    fbq: any;
  }
}

export interface PixelEventData {
  content_name?: string;
  content_category?: string;
  content_ids?: string[];
  content_type?: string;
  value?: number;
  currency?: string;
  num_items?: number;
  order_id?: string;
  search_string?: string;
  custom_data?: Record<string, any>;
}

class FacebookPixelService {
  private isInitialized = false;
  private isDevelopment = process.env.NODE_ENV === 'development';

  constructor() {
    this.checkInitialization();
  }

  private checkInitialization() {
    if (typeof window !== 'undefined' && window.fbq) {
      this.isInitialized = true;
      if (this.isDevelopment) {
        console.log('Facebook Pixel initialized successfully');
      }
    } else if (this.isDevelopment) {
      console.warn('Facebook Pixel not initialized');
    }
  }

  private safeTrack(eventName: string, data?: any) {
    try {
      if (this.isInitialized && window.fbq) {
        window.fbq('track', eventName, data);
        if (this.isDevelopment) {
          console.log(`Facebook Pixel: ${eventName}`, data);
        }
      } else if (this.isDevelopment) {
        console.log(`Facebook Pixel (disabled): ${eventName}`, data);
      }
    } catch (error) {
      if (this.isDevelopment) {
        console.error('Facebook Pixel error:', error);
      }
      // Silently fail in production to prevent breaking the app
    }
  }

  private safeTrackCustom(eventName: string, data?: any) {
    try {
      if (this.isInitialized && window.fbq) {
        window.fbq('trackCustom', eventName, data);
        if (this.isDevelopment) {
          console.log(`Facebook Pixel Custom: ${eventName}`, data);
        }
      } else if (this.isDevelopment) {
        console.log(`Facebook Pixel Custom (disabled): ${eventName}`, data);
      }
    } catch (error) {
      if (this.isDevelopment) {
        console.error('Facebook Pixel custom event error:', error);
      }
      // Silently fail in production to prevent breaking the app
    }
  }

  // Track page view
  trackPageView() {
    this.safeTrack('PageView');
  }

  // Track view content (product page views)
  trackViewContent(data: PixelEventData) {
    this.safeTrack('ViewContent', {
      content_name: data.content_name,
      content_category: data.content_category,
      content_ids: data.content_ids,
      content_type: data.content_type || 'product',
      value: data.value,
      currency: data.currency || 'MMK'
    });
  }

  // Track add to cart
  trackAddToCart(data: PixelEventData) {
    this.safeTrack('AddToCart', {
      content_name: data.content_name,
      content_category: data.content_category,
      content_ids: data.content_ids,
      content_type: data.content_type || 'product',
      value: data.value,
      currency: data.currency || 'MMK'
    });
  }

  // Track initiate checkout
  trackInitiateCheckout(data: PixelEventData) {
    this.safeTrack('InitiateCheckout', {
      content_category: data.content_category,
      content_ids: data.content_ids,
      content_type: data.content_type || 'product',
      value: data.value,
      currency: data.currency || 'MMK',
      num_items: data.num_items
    });
  }

  // Track purchase
  trackPurchase(data: PixelEventData) {
    this.safeTrack('Purchase', {
      content_ids: data.content_ids,
      content_type: data.content_type || 'product',
      value: data.value,
      currency: data.currency || 'MMK',
      num_items: data.num_items,
      order_id: data.order_id
    });
  }

  // Track search
  trackSearch(searchString: string) {
    this.safeTrack('Search', {
      search_string: searchString
    });
  }

  // Track add to wishlist
  trackAddToWishlist(data: PixelEventData) {
    this.safeTrack('AddToWishlist', {
      content_name: data.content_name,
      content_category: data.content_category,
      content_ids: data.content_ids,
      content_type: data.content_type || 'product',
      value: data.value,
      currency: data.currency || 'MMK'
    });
  }

  // Track custom events
  trackCustomEvent(eventName: string, data?: PixelEventData) {
    this.safeTrackCustom(eventName, data);
  }

  // Track lead (for contact forms, newsletter signup, etc.)
  trackLead(data?: PixelEventData) {
    this.safeTrack('Lead', data);
  }

  // Track complete registration
  trackCompleteRegistration(data?: PixelEventData) {
    this.safeTrack('CompleteRegistration', {
      content_name: data?.content_name,
      value: data?.value,
      currency: data?.currency || 'MMK'
    });
  }
}

// Export singleton instance
export const facebookPixel = new FacebookPixelService();

// Export individual tracking functions for convenience
export const {
  trackPageView,
  trackViewContent,
  trackAddToCart,
  trackInitiateCheckout,
  trackPurchase,
  trackSearch,
  trackAddToWishlist,
  trackCustomEvent,
  trackLead,
  trackCompleteRegistration
} = facebookPixel;
